import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const eur = (n) => `€${Number(n || 0).toLocaleString('en-EU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// --- Bootstrap prompt when the DB is empty --------------------------------

function BootstrapPrompt({ onDone }) {
  const [busy, setBusy] = useState(false);
  const boot = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/payroll/bootstrap`);
      toast.success('Payroll ready. Cyprus 2026 rates loaded.');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bootstrap failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg mx-auto mt-20 border border-zinc-200 bg-white p-8" data-testid="payroll-bootstrap">
      <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Set up Payroll</h2>
      <p className="text-sm text-zinc-500 mb-6">
        This creates your first Company record and seeds the Cyprus 2026 statutory rates
        (SI, GESY, HRDA, Redundancy, Social Cohesion, PAYE bands, insurable earnings ceilings).
        Every rate is editable afterwards, and every payslip freezes the rates that were
        effective on its payroll month.
      </p>
      <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={boot} disabled={busy} data-testid="payroll-bootstrap-btn">
        {busy ? 'Setting up…' : 'Initialise Payroll'}
      </Button>
    </div>
  );
}

// --- Dashboard ------------------------------------------------------------

function Dashboard({ companyId }) {
  const [d, setD] = useState(null);
  const [lv, setLv] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      axios.get(`${API}/payroll/dashboard?company_id=${companyId}`),
      axios.get(`${API}/payroll/leave/dashboard?company_id=${companyId}`),
    ]).then(([a, b]) => { setD(a.data); setLv(b.data); }).catch(() => {});
  }, [companyId]);
  if (!d) return <div className="text-zinc-500 text-sm">Loading…</div>;
  const cards = [
    { label: 'Total Employees', v: d.total_employees, testid: 'pd-total-emp' },
    { label: 'Active Employees', v: d.active_employees, testid: 'pd-active-emp' },
    { label: 'Paid This Month', v: d.employees_paid_this_month, testid: 'pd-paid-mo' },
    { label: 'Gross (This Month)', v: eur(d.total_gross_payroll_this_month), testid: 'pd-gross' },
    { label: 'Net (This Month)', v: eur(d.total_net_payroll_this_month), testid: 'pd-net' },
    { label: 'Employer Cost (This Month)', v: eur(d.total_employer_cost_this_month), testid: 'pd-er-cost' },
    { label: 'Outstanding Payroll', v: eur(d.outstanding_payroll_amount), testid: 'pd-outstanding' },
    { label: 'Outstanding Runs', v: d.outstanding_runs, testid: 'pd-out-runs' },
    { label: 'On Leave Today', v: lv?.employees_on_leave_today ?? 0, testid: 'pd-on-leave' },
    { label: 'Pending Leave', v: lv?.pending_leave_requests ?? 0, testid: 'pd-pending-leave' },
    { label: 'Pending Timesheets', v: lv?.pending_timesheets ?? 0, testid: 'pd-pending-ts' },
    { label: 'Upcoming Leave (14d)', v: lv?.upcoming_leave?.length ?? 0, testid: 'pd-upcoming-leave' },
  ];
  return (
    <div className="space-y-6" data-testid="payroll-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.label} className="border border-zinc-200 bg-white p-4" data-testid={c.testid}>
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{c.label}</p>
            <p className="text-2xl font-semibold mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.v}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-zinc-200 bg-white p-4">
          <h3 className="font-semibold text-sm mb-2">Upcoming Payroll</h3>
          {d.upcoming_payroll ? (
            <div className="text-sm">
              <p><b>#{d.upcoming_payroll.run_number}</b> — {d.upcoming_payroll.period_start} → {d.upcoming_payroll.period_end}</p>
              <p className="text-zinc-500">Status: <Badge variant="outline">{d.upcoming_payroll.status}</Badge></p>
              <p>Gross {eur(d.upcoming_payroll.total_gross)} · Net {eur(d.upcoming_payroll.total_net)}</p>
            </div>
          ) : <p className="text-sm text-zinc-500">No upcoming runs.</p>}
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <h3 className="font-semibold text-sm mb-2">Recent Payslips</h3>
          {(d.recent_payslips || []).length ? (
            <ul className="text-sm space-y-1">
              {d.recent_payslips.map(p => (
                <li key={p.payslip_id} className="flex justify-between border-b border-zinc-100 py-1">
                  <span>{p.employee_snapshot?.full_name || '?'} <span className="text-zinc-400 text-xs">({p.payslip_number})</span></span>
                  <span className="font-mono text-sm">{eur(p.net_salary)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-zinc-500">No payslips yet.</p>}
        </div>
      </div>
    </div>
  );
}

// --- Employees ------------------------------------------------------------

function Employees({ companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => {
    if (!companyId) return;
    axios.get(`${API}/payroll/employees?company_id=${companyId}`).then(r => setRows(r.data));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      await axios.post(`${API}/payroll/employees`, { ...form, company_id: companyId, base_salary: Number(form.base_salary || 0) });
      toast.success('Employee created'); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="payroll-employees">
      <div className="flex justify-between">
        <h3 className="font-semibold text-sm">Employees ({rows.length})</h3>
        <Button size="sm" className="rounded-none bg-zinc-900 text-white" onClick={() => setOpen(true)} data-testid="payroll-emp-add">+ Add Employee</Button>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Job Title</th>
              <th className="text-right px-3 py-2">Base Salary</th>
              <th className="text-left px-3 py-2">Frequency</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.employee_id} className="border-t border-zinc-100" data-testid={`payroll-emp-row-${r.employee_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.employee_number}</td>
                <td className="px-3 py-2">{r.full_name}</td>
                <td className="px-3 py-2">{r.job_title || '-'}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.base_salary)}</td>
                <td className="px-3 py-2">{r.payroll_frequency}</td>
                <td className="px-3 py-2"><Badge variant="outline">{r.status}</Badge></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="text-center text-zinc-400 py-8">No employees yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="payroll-emp-dialog">
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input value={form.first_name || ''} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first" /></div>
            <div><Label>Last Name *</Label><Input value={form.last_name || ''} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last" /></div>
            <div><Label>Job Title</Label><Input value={form.job_title || ''} onChange={e => setForm({ ...form, job_title: e.target.value })} /></div>
            <div><Label>Department</Label><Input value={form.department || ''} onChange={e => setForm({ ...form, department: e.target.value })} /></div>
            <div><Label>Hire Date</Label><Input type="date" value={form.hire_date || ''} onChange={e => setForm({ ...form, hire_date: e.target.value })} /></div>
            <div><Label>Base Salary *</Label><Input type="number" step="0.01" value={form.base_salary || ''} onChange={e => setForm({ ...form, base_salary: e.target.value })} data-testid="emp-salary" /></div>
            <div><Label>Payroll Frequency</Label>
              <Select value={form.payroll_frequency || 'monthly'} onValueChange={v => setForm({ ...form, payroll_frequency: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Bi-weekly</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Tax ID</Label><Input value={form.tax_id || ''} onChange={e => setForm({ ...form, tax_id: e.target.value })} /></div>
            <div><Label>Social Insurance No</Label><Input value={form.social_insurance_no || ''} onChange={e => setForm({ ...form, social_insurance_no: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-900 text-white" onClick={save} data-testid="emp-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Payroll Runs ---------------------------------------------------------

function PayrollRuns({ companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ payroll_frequency: 'monthly' });
  const load = useCallback(() => {
    if (!companyId) return;
    axios.get(`${API}/payroll/runs?company_id=${companyId}`).then(r => setRows(r.data));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      await axios.post(`${API}/payroll/runs`, { ...form, company_id: companyId });
      toast.success('Run created'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const calculate = async (id) => {
    try { const r = await axios.post(`${API}/payroll/runs/${id}/calculate`); toast.success(`Calculated · Gross ${eur(r.data.total_gross)}, Net ${eur(r.data.total_net)}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Calculation failed'); }
  };
  const transition = async (id, action) => {
    try {
      let body = {};
      if (action === 'unlock') {
        const reason = window.prompt('Reason for unlocking?'); if (!reason) return;
        body = { reason };
      }
      await axios.post(`${API}/payroll/runs/${id}/${action}`, body);
      toast.success(action); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="payroll-runs">
      <div className="flex justify-between">
        <h3 className="font-semibold text-sm">Payroll Runs ({rows.length})</h3>
        <Button size="sm" className="rounded-none bg-zinc-900 text-white" onClick={() => setOpen(true)} data-testid="payroll-run-add">+ New Run</Button>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Run #</th>
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-left px-3 py-2">Freq</th>
              <th className="text-right px-3 py-2">Employees</th>
              <th className="text-right px-3 py-2">Gross</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-right px-3 py-2">Er. Cost</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.run_id} className="border-t border-zinc-100" data-testid={`payroll-run-${r.run_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.run_number}</td>
                <td className="px-3 py-2 text-xs">{r.period_start} → {r.period_end}</td>
                <td className="px-3 py-2">{r.payroll_frequency}</td>
                <td className="px-3 py-2 text-right font-mono">{r.employee_count}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.total_gross)}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.total_net)}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.total_employer_cost)}</td>
                <td className="px-3 py-2"><Badge variant="outline">{r.status}</Badge></td>
                <td className="px-3 py-2 space-x-1 text-xs">
                  {r.status === 'draft' && <button onClick={() => calculate(r.run_id)} className="text-blue-700 hover:underline" data-testid={`run-calc-${r.run_id}`}>Calculate</button>}
                  {r.status === 'calculated' && <>
                    <button onClick={() => calculate(r.run_id)} className="text-blue-700 hover:underline">Recalculate</button>
                    <button onClick={() => transition(r.run_id, 'approve')} className="text-emerald-700 hover:underline" data-testid={`run-approve-${r.run_id}`}>Approve</button>
                  </>}
                  {r.status === 'approved' && <button onClick={() => transition(r.run_id, 'lock')} className="text-amber-700 hover:underline" data-testid={`run-lock-${r.run_id}`}>Lock</button>}
                  {r.status === 'locked' && <>
                    <button onClick={() => transition(r.run_id, 'unlock')} className="text-red-700 hover:underline">Unlock</button>
                    <button onClick={() => transition(r.run_id, 'mark-paid')} className="text-emerald-700 hover:underline">Mark Paid</button>
                  </>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="text-center text-zinc-400 py-8">No payroll runs yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>New Payroll Run</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Period Start *</Label><Input type="date" value={form.period_start || ''} onChange={e => setForm({ ...form, period_start: e.target.value })} data-testid="run-start" /></div>
            <div><Label>Period End *</Label><Input type="date" value={form.period_end || ''} onChange={e => setForm({ ...form, period_end: e.target.value })} data-testid="run-end" /></div>
            <div><Label>Payment Date</Label><Input type="date" value={form.payment_date || ''} onChange={e => setForm({ ...form, payment_date: e.target.value })} /></div>
            <div><Label>Frequency</Label>
              <Select value={form.payroll_frequency} onValueChange={v => setForm({ ...form, payroll_frequency: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Bi-weekly</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-900 text-white" onClick={create} data-testid="run-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Payslips (list + PDF) ------------------------------------------------

function Payslips({ companyId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/payroll/payslips?company_id=${companyId}`).then(r => setRows(r.data));
  }, [companyId]);
  const openPdf = (id) => window.open(`${API}/payroll/payslips/${id}/pdf`, '_blank');
  return (
    <div className="space-y-3" data-testid="payroll-payslips">
      <h3 className="font-semibold text-sm">Payslips ({rows.length})</h3>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Payslip #</th>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Period</th>
              <th className="text-right px-3 py-2">Gross</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-right px-3 py-2">Er. Cost</th>
              <th className="text-left px-3 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.payslip_id} className="border-t border-zinc-100" data-testid={`payslip-row-${p.payslip_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{p.payslip_number}</td>
                <td className="px-3 py-2">{p.employee_snapshot?.full_name || '?'}</td>
                <td className="px-3 py-2 text-xs">{p.period_start} → {p.period_end}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(p.gross_salary)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{eur(p.net_salary)}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(p.employer_cost)}</td>
                <td className="px-3 py-2"><Badge variant="outline">{p.status}</Badge></td>
                <td className="px-3 py-2"><button onClick={() => openPdf(p.payslip_id)} className="text-blue-700 hover:underline text-xs" data-testid={`payslip-pdf-${p.payslip_id}`}>PDF</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="text-center text-zinc-400 py-8">No payslips yet — calculate a Payroll Run first.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Payroll Settings -----------------------------------------------------

function Settings({ companyId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/payroll/settings?company_id=${companyId}`).then(r => setRows(r.data));
  }, [companyId]);
  return (
    <div className="space-y-3" data-testid="payroll-settings">
      <h3 className="font-semibold text-sm">Payroll Settings (versioned by effective date)</h3>
      {rows.map((s, i) => (
        <div key={s.settings_id} className="border border-zinc-200 bg-white p-4" data-testid={`settings-row-${s.settings_id}`}>
          <div className="flex justify-between mb-3">
            <div>
              <p className="font-semibold text-sm">{s.country_code} · {s.currency} · Effective from {s.effective_from} {s.effective_to ? `→ ${s.effective_to}` : '(current)'}</p>
              <p className="text-xs text-zinc-500">{s.notes || ''}</p>
            </div>
            {i === 0 && !s.effective_to && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">ACTIVE</Badge>}
          </div>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div><b>Employee SI:</b> {s.employee_social_insurance_pct}%</div>
            <div><b>Employee GESY:</b> {s.employee_gesy_pct}%</div>
            <div><b>Employer SI:</b> {s.employer_social_insurance_pct}%</div>
            <div><b>Employer GESY:</b> {s.employer_gesy_pct}%</div>
            <div><b>HRDA:</b> {s.employer_hrda_pct}%</div>
            <div><b>Redundancy Fund:</b> {s.employer_redundancy_fund_pct}%</div>
            <div><b>Social Cohesion Fund:</b> {s.employer_social_cohesion_pct}%</div>
            <div><b>Max Insurable (Mo.):</b> {eur(s.max_insurable_earnings_monthly)}</div>
            <div><b>Max Insurable (Wk.):</b> {eur(s.max_insurable_earnings_weekly)}</div>
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold text-zinc-500 uppercase mb-1">PAYE Bands</p>
            <table className="text-xs w-full max-w-md">
              <thead className="text-zinc-500">
                <tr><th className="text-left">From</th><th className="text-left">To</th><th className="text-right">Rate</th></tr>
              </thead>
              <tbody>
                {(s.paye_bands || []).map((b, j) => (
                  <tr key={j} className="border-t border-zinc-100">
                    <td className="font-mono">{eur(b.from)}</td>
                    <td className="font-mono">{b.to != null ? eur(b.to) : '∞'}</td>
                    <td className="font-mono text-right">{b.rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Leave tab -----------------------------------------------------------

function Leave({ companyId }) {
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: 'annual' });
  const year = new Date().getFullYear();
  const load = useCallback(async () => {
    if (!companyId) return;
    const [t, r, b, e] = await Promise.all([
      axios.get(`${API}/payroll/leave/types?company_id=${companyId}`),
      axios.get(`${API}/payroll/leave/requests?company_id=${companyId}`),
      axios.get(`${API}/payroll/leave/balances?company_id=${companyId}&year=${year}`),
      axios.get(`${API}/payroll/employees?company_id=${companyId}`),
    ]);
    setTypes(t.data); setRequests(r.data); setBalances(b.data); setEmployees(e.data);
  }, [companyId, year]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      await axios.post(`${API}/payroll/leave/requests`, { ...form, company_id: companyId });
      toast.success('Request created'); setOpen(false); setForm({ leave_type: 'annual' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const act = async (rid, action) => {
    try {
      let body = {};
      if (action === 'reject') { const r = window.prompt('Reject reason?'); if (!r) return; body = { reason: r }; }
      await axios.post(`${API}/payroll/leave/requests/${rid}/${action}`, body);
      toast.success(action); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-6" data-testid="payroll-leave">
      <div className="flex justify-between">
        <h3 className="font-semibold text-sm">Leave Requests ({requests.length})</h3>
        <Button size="sm" className="rounded-none bg-zinc-900 text-white" onClick={() => setOpen(true)} data-testid="leave-add-btn">+ New Request</Button>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 tracking-wider">
            <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Period</th><th className="text-right px-3 py-2">Days</th>
              <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {requests.map(r => {
              const emp = employees.find(e => e.employee_id === r.employee_id);
              return (
                <tr key={r.request_id} className="border-t border-zinc-100" data-testid={`leave-row-${r.request_id}`}>
                  <td className="px-3 py-2">{emp?.full_name || r.employee_id.slice(-6)}</td>
                  <td className="px-3 py-2">{r.leave_type}</td>
                  <td className="px-3 py-2 text-xs">{r.start_date} → {r.end_date}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.days}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{r.status}</Badge></td>
                  <td className="px-3 py-2 space-x-2 text-xs">
                    {r.status === 'submitted' && <>
                      <button className="text-emerald-700 hover:underline" onClick={() => act(r.request_id, 'approve')} data-testid={`leave-approve-${r.request_id}`}>Approve</button>
                      <button className="text-red-700 hover:underline" onClick={() => act(r.request_id, 'reject')}>Reject</button>
                    </>}
                    {['submitted', 'approved', 'draft'].includes(r.status) && <button className="text-zinc-500 hover:underline" onClick={() => act(r.request_id, 'cancel')}>Cancel</button>}
                  </td>
                </tr>
              );
            })}
            {!requests.length && <tr><td colSpan={6} className="text-center text-zinc-400 py-8">No leave requests.</td></tr>}
          </tbody>
        </table>
      </div>
      <div>
        <h3 className="font-semibold text-sm mb-2">Balances ({year})</h3>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 tracking-wider">
              <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Opening</th><th className="text-right px-3 py-2">Used</th>
                <th className="text-right px-3 py-2">Pending</th><th className="text-right px-3 py-2">Current</th></tr>
            </thead>
            <tbody>
              {balances.map(b => {
                const emp = employees.find(e => e.employee_id === b.employee_id);
                return (
                  <tr key={b.balance_id} className="border-t border-zinc-100">
                    <td className="px-3 py-2">{emp?.full_name || b.employee_id.slice(-6)}</td>
                    <td className="px-3 py-2">{b.leave_type}</td>
                    <td className="px-3 py-2 text-right font-mono">{b.opening_balance}</td>
                    <td className="px-3 py-2 text-right font-mono">{b.used}</td>
                    <td className="px-3 py-2 text-right font-mono">{b.pending}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{b.current_balance}</td>
                  </tr>
                );
              })}
              {!balances.length && <tr><td colSpan={6} className="text-center text-zinc-400 py-8">No balances yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>New Leave Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="rounded-none" data-testid="leave-emp-sel"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Type</Label>
              <Select value={form.leave_type} onValueChange={v => setForm({ ...form, leave_type: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Start Date *</Label><Input type="date" value={form.start_date || ''} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
            <div><Label>End Date *</Label><Input type="date" value={form.end_date || ''} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
            <div><Label>Reason</Label><Textarea value={form.reason || ''} onChange={e => setForm({ ...form, reason: e.target.value })} className="rounded-none" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-900 text-white" onClick={create} data-testid="leave-save">Save & Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Timesheets tab -----------------------------------------------------

function Timesheets({ companyId }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(async () => {
    if (!companyId) return;
    const [t, e] = await Promise.all([
      axios.get(`${API}/payroll/timesheets?company_id=${companyId}`),
      axios.get(`${API}/payroll/employees?company_id=${companyId}`),
    ]);
    setRows(t.data); setEmployees(e.data);
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      await axios.post(`${API}/payroll/timesheets`, { ...form, company_id: companyId,
        hours_worked: Number(form.hours_worked || 0),
        overtime_hours: Number(form.overtime_hours || 0) });
      toast.success('Timesheet added'); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const act = async (tid, action) => {
    try {
      let body = {};
      if (action === 'reject') { const r = window.prompt('Reject reason?'); if (!r) return; body = { reason: r }; }
      await axios.post(`${API}/payroll/timesheets/${tid}/${action}`, body);
      toast.success(action); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="payroll-timesheets">
      <div className="flex justify-between">
        <h3 className="font-semibold text-sm">Timesheets ({rows.length})</h3>
        <Button size="sm" className="rounded-none bg-zinc-900 text-white" onClick={() => setOpen(true)} data-testid="ts-add-btn">+ Add Timesheet</Button>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 tracking-wider">
            <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Date</th>
              <th className="text-right px-3 py-2">Hours</th><th className="text-right px-3 py-2">OT</th>
              <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(t => {
              const emp = employees.find(e => e.employee_id === t.employee_id);
              return (
                <tr key={t.timesheet_id} className="border-t border-zinc-100" data-testid={`ts-row-${t.timesheet_id}`}>
                  <td className="px-3 py-2">{emp?.full_name || t.employee_id.slice(-6)}</td>
                  <td className="px-3 py-2 text-xs">{t.date}</td>
                  <td className="px-3 py-2 text-right font-mono">{t.hours_worked}</td>
                  <td className="px-3 py-2 text-right font-mono">{t.overtime_hours}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{t.status}</Badge></td>
                  <td className="px-3 py-2 space-x-2 text-xs">
                    {t.status === 'draft' && <button className="text-blue-700 hover:underline" onClick={() => act(t.timesheet_id, 'submit')}>Submit</button>}
                    {t.status === 'submitted' && <>
                      <button className="text-emerald-700 hover:underline" onClick={() => act(t.timesheet_id, 'approve')} data-testid={`ts-approve-${t.timesheet_id}`}>Approve</button>
                      <button className="text-red-700 hover:underline" onClick={() => act(t.timesheet_id, 'reject')}>Reject</button>
                    </>}
                    {t.status === 'approved' && <button className="text-amber-700 hover:underline" onClick={() => act(t.timesheet_id, 'lock')}>Lock</button>}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={6} className="text-center text-zinc-400 py-8">No timesheets yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Add Timesheet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="rounded-none" data-testid="ts-emp-sel"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date *</Label><Input type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="ts-date" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hours Worked</Label><Input type="number" step="0.25" value={form.hours_worked || ''} onChange={e => setForm({ ...form, hours_worked: e.target.value })} data-testid="ts-hours" /></div>
              <div><Label>Overtime Hours</Label><Input type="number" step="0.25" value={form.overtime_hours || ''} onChange={e => setForm({ ...form, overtime_hours: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-900 text-white" onClick={create} data-testid="ts-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Root page with tabs -----------------------------------------------

export default function Payroll() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const { language } = useLanguage();
  const t_ = (el, en) => language === 'el' ? el : en;

  const loadCompanies = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payroll/companies`);
      setCompanies(r.data);
      if (!r.data.length) setNeedsBootstrap(true);
      else { setNeedsBootstrap(false); if (!companyId) setCompanyId(r.data[0].company_id); }
    } catch (e) { toast.error('Failed to load companies'); }
  }, [companyId]);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  if (needsBootstrap) return <Layout><BootstrapPrompt onDone={loadCompanies} /></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4" data-testid="payroll-root">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
              {t_('Μισθοδοσία', 'Payroll')}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t_('Ανεξάρτητο σύστημα μισθοδοσίας Κύπρου 2026.', 'Independent Cyprus 2026 payroll engine.')}
            </p>
          </div>
          {companies.length > 0 && (
            <Select value={companyId || undefined} onValueChange={setCompanyId}>
              <SelectTrigger className="rounded-none w-64" data-testid="payroll-company-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="dashboard" className="rounded-none" data-testid="payroll-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="employees" className="rounded-none" data-testid="payroll-tab-employees">Employees</TabsTrigger>
            <TabsTrigger value="runs" className="rounded-none" data-testid="payroll-tab-runs">Payroll Runs</TabsTrigger>
            <TabsTrigger value="payslips" className="rounded-none" data-testid="payroll-tab-payslips">Payslips</TabsTrigger>
            <TabsTrigger value="leave" className="rounded-none" data-testid="payroll-tab-leave">Leave</TabsTrigger>
            <TabsTrigger value="timesheets" className="rounded-none" data-testid="payroll-tab-timesheets">Timesheets</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none" data-testid="payroll-tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="mt-4"><Dashboard companyId={companyId} /></TabsContent>
          <TabsContent value="employees" className="mt-4"><Employees companyId={companyId} /></TabsContent>
          <TabsContent value="runs" className="mt-4"><PayrollRuns companyId={companyId} /></TabsContent>
          <TabsContent value="payslips" className="mt-4"><Payslips companyId={companyId} /></TabsContent>
          <TabsContent value="leave" className="mt-4"><Leave companyId={companyId} /></TabsContent>
          <TabsContent value="timesheets" className="mt-4"><Timesheets companyId={companyId} /></TabsContent>
          <TabsContent value="settings" className="mt-4"><Settings companyId={companyId} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
